// Adding the site to the home screen. Chrome and Edge (Android, desktop) fire beforeinstallprompt
// once the site can be installed; it's held here for our own button, in place of Chrome's banner,
// which would pop up mid-game. iPhones and iPads have no prompt: Safari's Share menu has
// Add to Home Screen, so there the button shows how.
import { useSyncExternalStore } from 'react'

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let held: InstallPromptEvent | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

/** Call before the app renders: the event can fire as soon as the page loads. */
export function captureInstallPrompt() {
  addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    held = e as InstallPromptEvent
    emit()
  })
  addEventListener('appinstalled', () => {
    held = null
    emit()
  })
}

/** Opened from the home screen (or as an installed app), not in a browser tab. */
export const isStandalone = () =>
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true

/** iPhone or iPad (iPadOS says it's a Mac, but has touch). */
export const isIos = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)

/** How this browser adds the site to the home screen: our button, Safari's Share menu, or not at all (or it's already there). */
export type InstallWay = 'prompt' | 'ios' | null

function way(): InstallWay {
  if (isStandalone()) return null
  if (held) return 'prompt'
  return isIos() ? 'ios' : null
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

export const useInstall = (): InstallWay => useSyncExternalStore(subscribe, way)

/** Shows the browser's install dialog; whether they added it. */
export async function install(): Promise<boolean> {
  const e = held
  if (!e) return false
  held = null
  emit()
  await e.prompt()
  return (await e.userChoice).outcome === 'accepted'
}
